import { API } from '@/script/api';
import kodepay from './kodepay';
import { i18n } from '@/i18n';
import { RouterPageConst, goRouterPage, routerPageURL } from '@/router/tool';
import { CommonUtility } from '@/script/utility';

class Payment {
  constructor() {
    this.isPageHide = false;
  }

  /**
   * 初始化
   */
  init() {
    kodepay.init();
    const ua = navigator.userAgent;
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua);
    if (isIOS) {
      window.addEventListener('pageshow', () => {
        if (this.isPageHide) {
          window.location.reload();
          this.isPageHide = false;
        }
      });
      window.addEventListener('pagehide', () => {
        this.isPageHide = true;
      });
    }
  }

  /**
   * 开启支付流程
   * @param {产品id} productId
   * @param {支付网关类型} gateway
   * @param {支付重定向回来路径} redirectPath
   * @param {付跳转前回调} toPurchaseCallback
   * @returns 支付结果
   */
  async start(productId, gateway, redirectPath, toPurchaseCallback) {
    try {
      if (gateway === 'kodepay') {
        return this.startWithKodePay(
          productId,
          toPurchaseCallback,
          redirectPath
        );
      } else if (gateway === 'useepay') {
        return this.startWithUseepay(
          productId,
          toPurchaseCallback,
          redirectPath
        );
      } else if (gateway === 'oceanpayment') {
        return this.startWithOceanpayment(
          productId,
          toPurchaseCallback,
          redirectPath
        );
      }
      return { status: false };
    } catch (_) {
      return { status: false };
    }
  }

  /**
   * 开启kodepay支付流程
   * @param {产品id} productId
   * @param {支付跳转前回调} toPurchaseCallback
   * @param {支付重定向回来路径} redirectPath
   * @returns 支付结果
   */
  async startWithKodePay(productId, toPurchaseCallback, redirectPath) {
    const resp = await API.createKodepaySession({ productId });
    if (resp.code === 200) {
      localStorage.setItem(
        'createTimestamp',
        `${parseInt(+new Date() / 1000)}`
      );
      const purchaseUrl = await kodepay.request(
        resp.data.kodepayProductId,
        resp.data.orderId,
        redirectPath
      );
      if (purchaseUrl) {
        if (toPurchaseCallback) {
          toPurchaseCallback(purchaseUrl);
        }
        return await this.goToPurchase(purchaseUrl);
      }
      return { status: false };
    }
    return {
      status: false,
      error: resp.message,
    };
  }

  /**
   * 开启Useepay支付流程
   * @param {产品id} productId
   * @param {支付跳转前回调} toPurchaseCallback
   * @param {支付重定向回来路径} redirectPath
   * @returns 支付结果
   */
  async startWithUseepay(productId, toPurchaseCallback, redirectPath) {
    const redirectURL = `${window.location.protocol}//${window.location.host}${redirectPath}`;
    try {
      const resp = await API.createUseepaySession({
        productId,
        reset_url: redirectURL,
      });
      if (resp.code === 200) {
        localStorage.setItem(
          'createTimestamp',
          `${parseInt(+new Date() / 1000)}`
        );
        if (resp.data.url) {
          if (toPurchaseCallback) {
            toPurchaseCallback();
          }
          return await this.goToPurchase(resp.data.url);
        }
        return { status: false };
      }
      return {
        status: false,
        error: resp.message,
      };
    } catch (_) {
      return { status: false };
    }
  }
  /**
   * 开启Oceanpayment支付流程
   * @param {产品id} productId
   * @param {支付跳转前回调} toPurchaseCallback
   * @param {支付重定向回来路径} redirectPath
   * @returns 支付结果
   */
  async startWithOceanpayment(productId, toPurchaseCallback, redirectPath) {
    const redirectURL = `${window.location.protocol}//${window.location.host}${redirectPath}`;
    try {
      const resp = await API.createOceanpaymentSession({
        productId,
        backUrl: redirectURL,
      });
      if (resp.code === 200) {
        localStorage.setItem(
          'createTimestamp',
          `${parseInt(+new Date() / 1000)}`
        );
        localStorage.setItem('oceanpayment', JSON.stringify(resp.data));
        if (toPurchaseCallback) {
          toPurchaseCallback();
        }
        const toURL = `${window.location.protocol}//${window.location.host
          }${routerPageURL(
            RouterPageConst.OceanpaymentWrapper,
            i18n.global.locale
          )}`;
        return await this.goToPurchase(toURL);
      }
      return {
        status: false,
        error: resp.message,
      };
    } catch (_) {
      return { status: false };
    }
  }

  /**
   * 跳转去网关支付
   * @param {网关支付url} url
   */
  async goToPurchase(url) {
    if (CommonUtility.isWebview) {
      this.goToPurchaseWithDirect(url);
      return true;
    }
    return this.goToPurchaseWithOpen(url);
  }

  /**
   * 直接跳转去网关支付
   * @param {网关支付url} url
   */
  async goToPurchaseWithDirect(url) {
    window.location.href = url;
  }

  /**
   * 开启新window去网关支付
   * @param {网关支付url} url
   */
  async goToPurchaseWithOpen(url) {
    return new Promise((resolve) => {
      let intervalObj;
      const purchaseWindow = window.open(url, '_blank');
      window.addEventListener('message', async (event) => {
        if (event.data?.status == null) return;
        clearInterval(intervalObj);
        setTimeout(() => purchaseWindow.close(), 300);
        if (event.data.status === '1' || event.data.status === 'success') {
          const res = await this.fetchLatestPurchaseStatus();
          resolve({ status: res });
        } else {
          resolve({ status: false });
        }
      });
      intervalObj = setInterval(() => {
        try {
          if (purchaseWindow.closed) {
            clearInterval(intervalObj);
            resolve({ status: false });
          }
        } catch (_) {
          this.goToPurchaseWithDirect(url);
        }
      }, 1000);
    });
  }

  /**
   * 处理支付网关重定向回来(中间跳板处理)
   * @param {url查询参数, 默认为空, 空则取window.location.search} search
   */
  async directBackGangplankHandle(url) {
    let params = new URLSearchParams(url || window.location.search);
    let status = params.get('status') || -1;
    try {
      if (window.opener && window !== window.opener) {
        console.info('window open 1 ->', status);
        window.opener.postMessage(
          { status },
          '*'
        );
      } else {
        console.info('window open 2 ->', status);
        window.localStorage.setItem(
          'paymentStatus',
          status === '1' || status === 'success' ? '1' : '-1'
        );
        goRouterPage(RouterPageConst.Pricing, i18n.global.locale);
      }
    } catch (_) {
      console.error('window open 3 ->', _);
      window.localStorage.setItem(
        'paymentStatus',
        status === '1' || status === 'success' ? '1' : '-1'
      );
      goRouterPage(RouterPageConst.Pricing, i18n.global.locale);
    }
  }

  /**
   * 是否需要处理上一次支付状态
   */
  needPurchaseResponseFromGangplank() {
    const status = window.localStorage.getItem('paymentStatus');
    if (status === '1' || status === 'success' || status === '-1') {
      return true;
    }
    return false;
  }

  /**
   * 从跳板跳转回来后，处理上一次支付状态
   */
  async purchaseResponseFromGangplank() {
    const status = window.localStorage.getItem('paymentStatus');
    if (!status) return false;
    window.localStorage.removeItem('paymentStatus');
    return await this.fetchLatestPurchaseStatus();
  }

  /**
   * 查询最后一次支付状态
   */
  async fetchLatestPurchaseStatus() {
    const timestampStr = localStorage.getItem('createTimestamp');
    let checkSuccess = false;
    try {
      const resp = await API.checkLastPurchaseOrderSuccess(timestampStr);
      if (resp.code === 200) {
        return true;
      }
      let counter = 0;
      while (!checkSuccess) {
        if (counter > 5) break;
        await new Promise((resolve) => {
          setTimeout(() => resolve(), 1000);
        });
        const resp = await API.checkLastPurchaseOrderSuccess(parseInt(timestampStr));
        checkSuccess = resp.code === 200;
        counter++;
      }
    } catch (_) {
      checkSuccess = false;
    }
    return checkSuccess;
  }

  /**
   * 表单形式提交POST请求
   * @param {请求目标地址} url
   * @param {POST请求参数} data
   * @param {是否开启新页签} newTab
   */
  formPostRequest(url, data, newTab) {
    const form = document.createElement('form');
    form.id = 'form';
    form.name = 'form';
    document.body.appendChild(form);
    for (const key in data) {
      const input = document.createElement('input');
      input.type = 'text';
      input.name = key;
      input.value = data[key];
      form.appendChild(input);
    }
    form.method = 'POST';
    form.action = url;
    if (newTab) {
      form.target = '_blank';
    }
    form.submit();
    document.body.removeChild(form);
  }
}
const payment = new Payment();
export default payment;
